import { z } from 'zod';
/**
 * `feed_visual_analyst` (spec 80 section 4.14, S200-1, AC-80-075): looks at
 * the images of the tenant's own published posts a person chose and proposes
 * brand colours, recurring motifs and fitting templates. Opt-in: the start
 * needs an explicit consent and the person's image selection; only the chosen
 * images reach the provider. The images are fetched and re-encoded in the
 * worker and never stored. The run has no effect: every proposal waits for a
 * person, and only colours can be applied (module 90's brand settings).
 */
export const FEED_VISUAL_LIMITS = {
    /** Posts read per account to find the chosen ones (newest first). */
    perAccountLimit: 50,
    /** Images a person may choose and the run sends at most. */
    maxImages: 20,
    /** Fewer chosen images are refused (422); fewer usable ones fail the run `too_few_posts`. */
    minImages: 6,
    /** Fetch limits of one image (spec 80 section 4.14). */
    maxImageBytes: 8 * 1024 * 1024,
    fetchTimeoutMs: 8_000,
    /** Longest edge of the re-encoded JPEG. */
    maxImageEdgePx: 1024,
    summaryMaxLength: 600,
    maxColors: 5,
    colorEvidenceMin: 1,
    evidenceMax: 5,
    maxMotifs: 8,
    motifLabelMaxLength: 60,
    motifEvidenceMin: 2,
    maxTemplates: 3,
    reasonMaxLength: 200,
    postIdMaxLength: 80,
    /** Picker candidates listed by `GET /v1/properties/{id}/feed-images` (T200-1-9). */
    candidateMax: 50,
    /** Caption excerpt of one candidate, in characters. */
    captionExcerptMaxLength: 120,
    /** Longest edge of a picker thumbnail (same-origin JPEG, never a Meta URL). */
    thumbnailEdgePx: 320,
};
/** An image reference of the context: `i1` is the newest chosen image. */
export const FEED_IMAGE_REF_PATTERN = /^i[1-9][0-9]?$/;
/** A Meta post ID as the Graph API returns it: digits, Facebook ones as `{page-id}_{post-id}`. */
export const OWN_POST_ID_PATTERN = /^[0-9]+(_[0-9]+)?$/;
/** The brand colour slots a run may propose (module 90's `BRAND_COLOR_KEYS`). */
export const FEED_VISUAL_COLOR_SLOTS = ['primary', 'secondary', 'accent', 'background', 'text'];
/** One own post a person chose for the visual analysis. */
export const feedVisualPostSchema = z.strictObject({
    platform: z.enum(['instagram', 'facebook']),
    post_id: z.string().min(1).max(FEED_VISUAL_LIMITS.postIdMaxLength).regex(OWN_POST_ID_PATTERN),
});
/**
 * Run parameters: the property, the explicit consent (always `true`; the start
 * refuses anything else) and the chosen posts, unique, 6 to 20.
 */
export const feedVisualAnalystInputSchema = z.strictObject({
    property_id: z.uuid(),
    consent: z.literal(true),
    posts: z
        .array(feedVisualPostSchema)
        .min(FEED_VISUAL_LIMITS.minImages)
        .max(FEED_VISUAL_LIMITS.maxImages)
        .refine((posts) => new Set(posts.map((post) => `${post.platform}:${post.post_id}`)).size === posts.length, { message: 'each post at most once' }),
});
const imageRef = z.string().regex(FEED_IMAGE_REF_PATTERN);
const evidence = (min) => z.array(imageRef).min(min).max(FEED_VISUAL_LIMITS.evidenceMax);
const text = (max) => z.string().trim().min(1).max(max);
export const feedVisualAnalystOutputSchema = z.object({
    summary: text(FEED_VISUAL_LIMITS.summaryMaxLength),
    colors: z
        .array(z.object({ slot: z.enum(FEED_VISUAL_COLOR_SLOTS), hex: z.string().regex(/^#[0-9A-F]{6}$/), evidence: evidence(FEED_VISUAL_LIMITS.colorEvidenceMin) }))
        .max(FEED_VISUAL_LIMITS.maxColors),
    motifs: z.array(z.object({ label: text(FEED_VISUAL_LIMITS.motifLabelMaxLength), evidence: evidence(FEED_VISUAL_LIMITS.motifEvidenceMin) })).max(FEED_VISUAL_LIMITS.maxMotifs),
    templates: z.array(z.object({ template_key: z.string().min(1).max(80), reason: text(FEED_VISUAL_LIMITS.reasonMaxLength) })).max(FEED_VISUAL_LIMITS.maxTemplates),
});
/** Proposal keys (spec 80 section 4.14): `color.<slot>`, `motif.<index>`, `template.<key>`. */
export function feedVisualProposalKeys(stored) {
    return [
        ...stored.colors.map((color) => `color.${color.slot}`),
        ...stored.motifs.map((_motif, index) => `motif.${index}`),
        ...stored.templates.map((template) => `template.${template.template_key}`),
    ];
}
//# sourceMappingURL=feed-visual-analyst.js.map